import type { ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { OptionState, type OptionCss } from './types';
import { OPTION_LETTERS } from '@/constants/ui';

const DEFAULT_CSS = {
  base: 'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition disabled:cursor-default',
  letter: 'flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold',
  text: 'flex-1',
  state: {
    [OptionState.DEFAULT]: 'border-slate-200 text-slate-700 hover:border-brand-400',
    [OptionState.SELECTED]: 'border-brand-500 bg-brand-50 text-brand-900',
    [OptionState.CORRECT]: 'border-emerald-500 bg-emerald-50 text-emerald-900',
    [OptionState.INCORRECT]: 'border-red-400 bg-red-50 text-red-500 line-through',
    [OptionState.MUTED]: 'border-slate-200 text-slate-400'
  } as Record<OptionState, string>,
  letterState: {
    [OptionState.DEFAULT]: 'bg-slate-100 text-slate-500',
    [OptionState.SELECTED]: 'bg-brand-500 text-white',
    [OptionState.CORRECT]: 'bg-emerald-500 text-white',
    [OptionState.INCORRECT]: 'bg-red-400 text-white',
    [OptionState.MUTED]: 'bg-slate-100 text-slate-400'
  } as Record<OptionState, string>
};

interface OptionProps {
  index: number;
  label: string;
  state?: OptionState;
  disabled?: boolean;
  showLetter?: boolean;
  onSelect?: (index: number) => void;
  css?: OptionCss;
  trailing?: ReactNode;
}

export function Option({
  index,
  label,
  state = OptionState.DEFAULT,
  disabled,
  showLetter = true,
  onSelect,
  css,
  trailing
}: OptionProps) {
  const buttonClass = `${css?.base ?? DEFAULT_CSS.base} ${css?.state?.[state] ?? DEFAULT_CSS.state[state]}`;
  const letterClass = `${css?.letter ?? DEFAULT_CSS.letter} ${css?.letterState?.[state] ?? DEFAULT_CSS.letterState[state]}`;

  const defaultTrailing =
    state === OptionState.CORRECT ? (
      <Check size={16} className="text-emerald-600" />
    ) : state === OptionState.INCORRECT ? (
      <X size={16} className="text-red-500" />
    ) : null;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect?.(index)}
      className={buttonClass}
    >
      {showLetter && <span className={letterClass}>{OPTION_LETTERS[index] ?? index + 1}</span>}
      <span className={css?.text ?? DEFAULT_CSS.text}>{label}</span>
      {trailing ?? defaultTrailing}
    </button>
  );
}
